import { useEffect } from 'react';

import { isFirebaseConfigured } from '@/services/firebase/config';
import { watchAuthState } from '@/services/firebase/auth';
import { clearCachedKey, loadCachedKey } from '@/services/crypto/keyCache';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settingsStore';

export function useFirebaseAuth() {
  const setUser = useAuthStore((s) => s.setUser);
  const setEncryptionKey = useAuthStore((s) => s.setEncryptionKey);
  const setAuthReady = useAuthStore((s) => s.setAuthReady);
  const reset = useAuthStore((s) => s.reset);
  const syncEnabled = useSettingsStore((s) => s.syncEnabled);

  useEffect(() => {
    if (!isFirebaseConfigured() || !syncEnabled) {
      setAuthReady(true);
      return;
    }
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    void (async () => {
      try {
        unsubscribe = await watchAuthState(async (user) => {
          if (cancelled) return;
          if (user) {
            setUser({ uid: user.uid, email: user.email, displayName: user.displayName });
            try {
              const cached = await loadCachedKey(user.uid);
              if (cached && !cancelled) {
                setEncryptionKey(cached.key, cached.salt);
              }
            } catch (err) {
              console.error('Nie można odczytać zapisanego klucza szyfrującego', err);
            }
          } else {
            reset();
            void clearCachedKey().catch(() => undefined);
          }
          setAuthReady(true);
        });
      } catch (err) {
        console.error('Nie można zainicjować Firebase Auth', err);
        setAuthReady(true);
      }
    })();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [setUser, setEncryptionKey, setAuthReady, reset, syncEnabled]);
}
